import type { SuccessfulEventOutput } from "socket-call-client";

import type { ClientEvents as CoaClientEvents } from "~dm-services/coa";
import type { InducksIssueDetails } from "~dm-types/InducksIssueDetails";
import type { InducksIssueQuotationSimple } from "~dm-types/InducksIssueQuotationSimple";
import type {
  inducks_story,
  inducks_storyversion,
  IssuecodeDetail,
} from "~prisma-schemas/schemas/coa";

import { socketInjectionKey } from "../composables/useDmSocket";
import { getCurrentLocaleShortKey } from "../composables/useLocales";

const addPartInfo = (issueDetails: InducksIssueDetails) => {
  const storyPartCounter = Object.entries(
    issueDetails.entries.reduce<{ [storycode: string]: number }>(
      (acc, { storycode }) => {
        if (storycode) {
          acc[storycode] = (acc[storycode] || 0) + 1;
        }
        return acc;
      },
      {},
    ),
  )
    .filter(([, occurrences]) => occurrences > 1)
    .reduce<{ [storycode: string]: number }>((acc, [storycode]) => {
      acc[storycode] = 1;
      return acc;
    }, {});

  return {
    ...issueDetails,
    entries: issueDetails.entries.map((entry) => ({
      ...entry,
      part: storyPartCounter[entry.storycode]
        ? storyPartCounter[entry.storycode]++
        : null,
    })),
  };
};

const mergeInto = <T extends Record<string, unknown>>(
  target: Ref<T>,
  patch: Partial<T>,
) => {
  target.value = { ...toRaw(target.value), ...patch } as T;
};

export const coa = defineStore("coa", () => {
  const { coa: events } = inject(socketInjectionKey)!;

  const locale = useI18n().locale,
    coverUrls = shallowRef<{ [issuecode: string]: string }>({}),
    countryNames =
      shallowRef<SuccessfulEventOutput<CoaClientEvents, "getCountryList">>(),
    publicationNames = shallowRef<
      SuccessfulEventOutput<
        CoaClientEvents,
        "getPublicationListFromCountrycodes"
      >
    >({}),
    publicationNamesFullCountries = shallowRef<string[]>([]),
    personNames =
      shallowRef<SuccessfulEventOutput<CoaClientEvents, "getAuthorList">>(),
    issueDetails = ref<{ [issuecode: string]: InducksIssueDetails }>({}),
    isLoadingCountryNames = ref(false),
    issuecodeDetails = ref<Record<string, IssuecodeDetail>>({}),
    issuePopularities = shallowRef<
      SuccessfulEventOutput<CoaClientEvents, "getIssuePopularities">
    >({}),
    issuecodesByPublicationcode = ref<
      SuccessfulEventOutput<CoaClientEvents, "getIssuecodesByPublicationcodes">
    >({}),
    issuesByPublicationcode = ref<
      Record<
        string,
        SuccessfulEventOutput<CoaClientEvents, "getIssuesByPublicationcode">
      >
    >({}),
    issueCountsByCountrycode = ref<
      SuccessfulEventOutput<CoaClientEvents, "getCoaCountByCountrycode">
    >({}),
    issueCountsByPublicationcode = ref<
      SuccessfulEventOutput<CoaClientEvents, "getCoaCountByPublicationcode">
    >({}),
    issueQuotations = ref<
      SuccessfulEventOutput<
        CoaClientEvents,
        "getQuotationsByIssuecodes"
      >["quotations"]
    >({}),
    storyDetails = ref<Record<string, inducks_story>>({}),
    storyUrls = ref<
      SuccessfulEventOutput<CoaClientEvents, "getStoryDetails">["storyUrls"]
    >({}),
    storyversionDetails = ref<Record<string, inducks_storyversion>>({}),
    addPublicationNames = (
      newPublicationNames: typeof publicationNames.value,
    ) => {
      publicationNames.value = {
        ...publicationNames.value,
        ...newPublicationNames,
      };
    },
    setPersonNames = (newPersonNames: { [personcode: string]: string }) => {
      if (!personNames.value) {
        personNames.value = {};
      }
      personNames.value = Object.assign(personNames.value, newPersonNames);
    },
    setCoverUrl = (issuecode: string, url: string) => {
      coverUrls.value[issuecode] = url;
    },
    fetchIssueQuotations = async (issuecodes: string[]) => {
      const existingIssuecodes = new Set(
        Object.keys(issueQuotations.value || {}),
      );
      const newIssuecodes = issuecodes.filter(
        (issuecode) => !existingIssuecodes.has(issuecode),
      );
      if (newIssuecodes.length) {
        await events
          .getQuotationsByIssuecodes(newIssuecodes)
          .then((newIssueQuotations) => {
            addIssueQuotations(newIssueQuotations.quotations);
          })
          .catch((e) => {
            console.error(e.error);
          });
      }
    },
    addIssueQuotations = (
      newIssueQuotations: Record<string, InducksIssueQuotationSimple>,
    ) => {
      issueQuotations.value = Object.assign(
        {},
        toRaw(issueQuotations.value),
        newIssueQuotations,
      );
    },
    fetchCountryNames = async (ignoreCache = false) => {
      if (
        (!isLoadingCountryNames.value && !countryNames.value) ||
        ignoreCache
      ) {
        isLoadingCountryNames.value = true;
        countryNames.value = await events.getCountryList(
          getCurrentLocaleShortKey(locale.value),
          [],
        );
        isLoadingCountryNames.value = false;
      }
    },
    fetchPublicationNames = async (newPublicationCodes: string[]) => {
      const actualNewPublicationCodes = [
        ...new Set(
          newPublicationCodes.filter(
            (publicationcode) =>
              publicationcode &&
              !Object.keys(publicationNames.value).includes(publicationcode),
          ),
        ),
      ];
      return (
        actualNewPublicationCodes.length &&
        addPublicationNames(
          await events.getPublicationListFromPublicationcodeList(
            actualNewPublicationCodes,
          ),
        )
      );
    },
    fetchPublicationNamesFromCountry = async (countrycode: string) =>
      publicationNamesFullCountries.value.includes(countrycode)
        ? void 0
        : events
            .getPublicationListFromCountrycodes([countrycode])
            .then((data) => {
              addPublicationNames(data);
              publicationNamesFullCountries.value = [
                ...publicationNamesFullCountries.value,
                countrycode,
              ];
            }),
    fetchPersonNames = async (newPersonCodes: string[]) => {
      const actualNewPersonCodes = [
        ...new Set(
          newPersonCodes.filter(
            (personCode) =>
              !Object.keys(personNames.value || {}).includes(personCode),
          ),
        ),
      ];
      return (
        actualNewPersonCodes.length &&
        setPersonNames(await events.getAuthorList(actualNewPersonCodes))
      );
    },
    fetchIssuecodeDetails = async (
      issuecodes: string[],
      withFields: ("title" | "fullyindexed")[] = [],
    ) => {
      const newIssuecodes = issuecodes.filter(
        (issuecode) =>
          !(issuecode in issuecodeDetails.value) ||
          withFields.some(
            (field) => !(field in issuecodeDetails.value[issuecode]),
          ),
      );
      if (newIssuecodes.length) {
        mergeInto(
          issuecodeDetails,
          await events.getIssues(newIssuecodes, withFields),
        );
      }
    },
    fetchIssuePopularities = async (issuecodes: string[]) => {
      const existingIssuecodes = new Set(
        Object.keys(issuePopularities.value || {}),
      );
      const newIssuecodes = issuecodes.filter(
        (issuecode) => !existingIssuecodes.has(issuecode),
      );
      if (newIssuecodes.length) {
        Object.assign(
          issuePopularities.value,
          await events.getIssuePopularities(newIssuecodes),
        );
      }
    },
    fetchStoryDetails = async (storycodes: string[]) => {
      const existingStorycodes = new Set(Object.keys(storyDetails.value || {}));
      const newStorycodes = storycodes.filter(
        (storycode) => !existingStorycodes.has(storycode),
      );
      if (newStorycodes.length) {
        await events
          .getStoryDetails(newStorycodes)
          .then((newStoryDetails) => {
            storyDetails.value = {
              ...toRaw(storyDetails.value),
              ...newStoryDetails.stories,
            };
            storyUrls.value = {
              ...toRaw(storyUrls.value),
              ...newStoryDetails.storyUrls,
            };
          })
          .catch((e) => {
            console.error(e.error);
          });
      }
    },
    fetchStoryversionDetails = async (storyversioncodes: string[]) => {
      const existingStoryversioncodes = new Set(
        Object.keys(storyversionDetails.value || {}),
      );
      const newStoryversioncodes = storyversioncodes.filter(
        (storyversion) => !existingStoryversioncodes.has(storyversion),
      );
      if (newStoryversioncodes.length) {
        await events
          .getStoryversionsDetails(newStoryversioncodes)
          .then((newStoryversionDetails) => {
            storyversionDetails.value = {
              ...toRaw(storyversionDetails.value),
              ...newStoryversionDetails.storyversions,
            };
          })
          .catch((e) => {
            console.error(e.error);
          });
      }
    },
    fetchIssuecodesByPublicationcode = async (publicationcodes: string[]) => {
      const existingPublicationcodes = new Set(
        Object.keys(issuecodesByPublicationcode.value || {}),
      );
      const newPublicationcodes = new Set(
        publicationcodes.filter(
          (publicationcode) => !existingPublicationcodes.has(publicationcode),
        ),
      );

      if (newPublicationcodes.size) {
        mergeInto(
          issuecodesByPublicationcode,
          await events.getIssuecodesByPublicationcodes(
            Array.from(newPublicationcodes),
          ),
        );
      }
    },
    fetchIssuesByPublicationcode = async (publicationcode: string) => {
      if (!(publicationcode in issuesByPublicationcode.value)) {
        issuesByPublicationcode.value[publicationcode] =
          await events.getIssuesByPublicationcode(publicationcode);
      }
    },
    fetchIssueCountsByCountrycode = async (countrycodes: string[]) => {
      const filteredCountrycodes = countrycodes.filter(
        (countrycode) => !(countrycode in issueCountsByCountrycode.value),
      );
      if (filteredCountrycodes.length) {
        mergeInto(
          issueCountsByCountrycode,
          await events.getCoaCountByCountrycode(filteredCountrycodes),
        );
      }
    },
    fetchIssueCountsByPublicationcode = async (publicationcodes: string[]) => {
      const filteredPublicationcodes = publicationcodes.filter(
        (publicationcode) =>
          !(publicationcode in issueCountsByPublicationcode.value),
      );
      if (filteredPublicationcodes.length) {
        mergeInto(
          issueCountsByPublicationcode,
          await events.getCoaCountByPublicationcode(filteredPublicationcodes),
        );
      }
    },
    fetchRecentIssues = () => events.getRecentIssues(),
    fetchCoverUrls = (publicationcode: string) =>
      events.getIssueCoverDetailsByPublicationcode(publicationcode),
    fetchCoverUrlsByIssuecodes = (issuecodes: string[]) =>
      events.getIssueCoverDetails(issuecodes),
    fetchIssueUrls = async (issuecode: string) => {
      if (!(issuecode in issueDetails.value)) {
        await events
          .getIssueDetails(issuecode)
          .then((newIssueDetails) => {
            issueDetails.value = {
              ...toRaw(issueDetails.value),
              [issuecode]: addPartInfo(newIssueDetails),
            };
          })
          .catch((e) => {
            console.error(e.error);
          });
      }
    };

  return {
    addIssueQuotations,
    addPublicationNames,
    countryNames,
    coverUrls,
    events,
    fetchCountryNames,
    fetchCoverUrls,
    fetchCoverUrlsByIssuecodes,
    fetchIssuecodeDetails,
    fetchIssuecodesByPublicationcode,
    fetchIssuesByPublicationcode,
    fetchIssueCountsByCountrycode,
    fetchIssueCountsByPublicationcode,
    fetchIssuePopularities,
    fetchIssueQuotations,
    fetchIssueUrls,
    fetchPersonNames,
    fetchPublicationNames,
    fetchPublicationNamesFromCountry,
    fetchRecentIssues,
    fetchStoryDetails,
    fetchStoryversionDetails,

    isLoadingCountryNames,
    issuecodeDetails,
    issuecodesByPublicationcode,
    issueCountsByCountrycode,
    issueCountsByPublicationcode,
    issueDetails,
    issuePopularities: issuePopularities,
    issueQuotations,
    issuesByPublicationcode,
    personNames,
    publicationNames,
    publicationNamesFullCountries,
    setCoverUrl,
    setPersonNames,
    storyDetails,
    storyUrls,
    storyversionDetails,
  };
});
